import { useState, FormEvent } from "react";
import { HelpCircle, Pencil } from "lucide-react";

interface ClarifyingQuestionProps {
  query: string;
  options: string[];
  onSelect: (option: string) => void;
  loadingOptions?: boolean;
}

export default function ClarifyingQuestion({ query, options, onSelect, loadingOptions }: ClarifyingQuestionProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState("");

  const submitCustom = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = custom.trim();
    if (!trimmed) return;
    onSelect(trimmed);
  };

  return (
    <div className="max-w-2xl mx-auto py-8 sm:py-16 animate-slide-in-up px-1">
      <div className="retro-border retro-shadow bg-card p-4 sm:p-8">
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
          <p className="font-mono text-xs sm:text-sm text-muted-foreground">
            Good question — just need a bit more direction.
          </p>
        </div>
        <p className="font-mono text-sm sm:text-lg mb-4 sm:mb-6">
          You asked: <span className="text-primary font-bold">"{query}"</span>
        </p>
        <p className="text-xs font-mono text-muted-foreground mb-3 sm:mb-4">
          {loadingOptions ? "Thinking up the most relevant angles…" : "What matters most?"}
        </p>
        <div className="space-y-1.5 sm:space-y-2 stagger-children">
          {options.map((option) => (
            <button
              key={option}
              onClick={() => onSelect(option)}
              className="w-full text-left font-mono text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-3 retro-border bg-background hover:bg-muted hover:border-primary transition-all hover-lift"
            >
              → {option}
            </button>
          ))}

          {/* "Something else" escape hatch — opens an inline text field */}
          {!showCustom ? (
            <button
              type="button"
              onClick={() => setShowCustom(true)}
              className="w-full text-left font-mono text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-3 retro-border border-dashed bg-accent/10 hover:bg-accent/20 hover:border-accent transition-all hover-lift flex items-center gap-2"
            >
              <Pencil className="w-3.5 h-3.5 text-accent flex-shrink-0" />
              <span>Something else — let me describe it</span>
            </button>
          ) : (
            <form onSubmit={submitCustom} className="retro-border bg-background p-2.5 sm:p-3 space-y-2">
              <label className="block font-mono text-[10px] sm:text-xs text-muted-foreground">
                Tell us a bit more about what you're looking for:
              </label>
              <input
                autoFocus
                type="text"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder={`e.g. key takeaways from ${query}`}
                className="w-full font-mono text-xs sm:text-sm px-3 py-2 bg-card border-2 border-border focus:border-primary focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={!custom.trim()}
                  className="comic-play-btn relative overflow-hidden font-mono font-bold text-xs sm:text-sm px-4 py-2 bg-primary text-primary-foreground border-[3px] border-[hsl(24,80%,35%)] rounded-md shadow-[3px_3px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="relative z-10">Search this →</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCustom(false); setCustom(""); }}
                  className="font-mono text-xs sm:text-sm px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
